package gr.tech.GRAttestation.util

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.AnimationIterationCount
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.animation
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.icons.fa.*
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.varabyte.kobweb.silk.style.toModifier
import gr.tech.GRAttestation.styles.PDFLoadingSpinnerAnimation
import gr.tech.GRAttestation.styles.PDFStatusTextStyle
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.AnimationTimingFunction
import org.jetbrains.compose.web.css.ms
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement
import org.w3c.files.Blob
import org.w3c.files.FileReader

/*
    HTML2PDF NOTES:

    Possible to fail with 'Too Many Pages' error -
    The error is from the HTML canvas element getting too big (px Height in this case).
    See: https://github.com/eKoopmans/html2pdf.js/issues/19
*/

private val scope = MainScope()

enum class PDFGenStatus {
    GENERATING,
    INSTANCE_READY,
    BLOB_READY,
    READY_DOWNLOAD,
    FAILED,
}

class GeneratePDFTool {
    var pdfInstance: dynamic = null

    fun genPDF(
        listOfIds: List<String>,
        onGenerationStatus: (PDFGenStatus) -> Unit,
        receipt: Boolean = false
    ) {
        try {
            scope.launch {
                listOfIds.forEach { id ->
                    window.setTimeout({
                        val element = document.getElementById(id) as HTMLElement? // Element is retrieved
                        if (element != null) {
                            val instance = if (receipt) {
                                js(
                                    code = """
                                        html2pdf()
                                            .from(element)
                                            .set({
                                                filename: 'AttestationForm.pdf',
                                                image: { type: 'jpeg', quality: 0.99 },
                                                html2canvas: {
                                                    logging: true,
                                                    scale: 1.2,
                                                    scrollY: 0, 
                                                    letterRendering: true,
                                                },
                                                jsPDF: { unit: 'px', format: [970, 1100], orientation: 'portrait', compress: true, hotfixes: ["px_scaling"] }
                                            })
                                        """
                                )
                            } else {
                                js(
                                    code = """
                                    html2pdf()
                                        .from(element)
                                        .set({
                                            filename: 'AttestationForm.pdf',
                                            image: { type: 'jpeg', quality: 0.99 },
                                            html2canvas: {
                                                logging: false,
                                                scale: 2, 
                                                scrollY: 0, 
                                                letterRendering: true,
                                            },
                                            jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait', compress: true }
                                        })
                                    """
                                )
                            }

                            // Literally saving this from JS into our KotlinJS code!
                            js("this.pdfInstance = instance")
                            instance.then {
                                onGenerationStatus(PDFGenStatus.INSTANCE_READY)
                            }.catch {
                                onGenerationStatus(PDFGenStatus.FAILED)
                            }
                        } else {
                            onGenerationStatus(PDFGenStatus.FAILED)
                        }
                    }, 500)
                }
            }
        } catch (e: Exception) {
            console.error(e)
            onGenerationStatus(PDFGenStatus.FAILED)
        }
    }

    fun downloadPDFtoBrowser(onError: () -> Unit = {}) {
        val instance = js("pdfInstance") ?: run {
            console.warn("No generated PDF instance found.")
            onError()
            return
        }

        instance.save().catch {
            console.error("Failed to Download PDF!")
            onError()
        }
    }

    fun getBlob(onBlobReady: (Blob) -> Unit, onError: () -> Unit = {}) {
        console.log("Getting PDF blob...")
        val instance = js("pdfInstance") ?: run {
            console.warn("No generated PDF instance found.")
            onError()
            return
        }

        instance.outputPdf("blob").then { blob ->
            console.log("Blob retrieved")
            onBlobReady(blob as Blob)
        }.catch { err ->
            console.error("Failed to get PDF blob: $err")
            onError()
        }
    }
}

fun PdfAsBase64(blob: Blob) {
    // For example: convert to Base64
    val reader = FileReader()
    reader.onloadend = {
        val base64Data = reader.result as String
        console.log("Base64 PDF:", base64Data)
    }
    reader.readAsDataURL(blob)
}


/*
0 - Pending/Loading/Generating
1 - Complete/Finished/Worked
-1 - Error/Not Finished/Didn't Work
*/
@Composable
fun PDFStatusText(
    statusInt: Int
) {
    H3(
        attrs = PDFStatusTextStyle.toModifier()
            .toAttrs()
    ) {
        when (statusInt) {
            0 -> {
                FaCircleNotch(Modifier
                    .animation(
                        PDFLoadingSpinnerAnimation
                            .toAnimation(
                                duration = 1500.ms,
                                timingFunction = AnimationTimingFunction.Linear,
                                iterationCount = AnimationIterationCount.Infinite,
                            )
                    )
                    .margin(right = 6.px))
                Text("Generating")
            }
            1 -> {
                FaFloppyDisk(Modifier.margin(right = 4.px), style = IconStyle.FILLED)
                Text("Ready to Save")
            }
            2 -> {
                FaCircleNotch(Modifier
                    .animation(
                        PDFLoadingSpinnerAnimation
                            .toAnimation(
                                duration = 1500.ms,
                                timingFunction = AnimationTimingFunction.Linear,
                                iterationCount = AnimationIterationCount.Infinite,
                            )
                    )
                    .margin(right = 6.px))
                Text("Saving")
            }
            3 -> {
                FaCheck(Modifier.margin(right = 4.px))
                Text("Complete")
            }
            -1 -> {
                FaXmark(Modifier.margin(right = 4.px))
                Text("Failed!")
            }
        }
    }
}

